// src/RoleProtectedRoute.js
import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { Navigate } from "react-router-dom";

export default function RoleProtectedRoute({ allowedRole, children }) {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true); // loading auth + role

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        // not logged in
        setUser(null);
        setUserRole(null);
        setLoading(false);
        return;
      }

      setUser(firebaseUser);

      try {
        // fetch role from /users/{uid}
        const snap = await getDoc(doc(db, "users", firebaseUser.uid));
        if (snap.exists()) {
          const data = snap.data();
          setUserRole(data?.role || null);
        } else {
          // user document missing
          setUserRole(null);
        }
      } catch (err) {
        console.error("Error fetching user role:", err);
        setUserRole(null);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // Still loading auth/role
  if (loading) {
    return (
      <div style={{ display: "grid", placeItems: "center", minHeight: "100vh" }}>
        Checking access...
      </div>
    );
  }

  // Not logged in → go to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Logged in but NO role in users doc
  // 👉 You can change "/complete-profile" to whatever onboarding route you have.
  if (!userRole) {
    console.warn("User is logged in but has no role set in /users doc");
    return <Navigate to="/login" replace />;
    // or: return <Navigate to="/complete-profile" replace />;
  }

  // Role mismatch → send them to their own dashboard
  if (userRole !== allowedRole) {
    return <Navigate to={`/${getDashboardRoute(userRole)}`} replace />;
  }

  // Authorized → render children
  return children;
}

function getDashboardRoute(role) {
  switch (role) {
    case "admin":
      return "admin-dashboard";
    case "doctor":
      return "dashboard-doctor";
    case "clinic":
      return "dashboard-clinic";
    case "patient":
      return "dashboard-patient";
    default:
      return "login";
  }
}
